import { convertDates, toEnDigit } from 'utils';
import { RAYCHAT_UPLOAD_FILE_URL } from 'utils/constants';
import { getDecodedURL } from 'utils/url';

// at the moment the api accepts date in jalali date and in this format: '1402/02/24'
export const getProperDate = (time) => {
  return new Date(time).toLocaleDateString('fa-IR');
};

const getDateBasedOnLocale = (date) => {
  const gregorianDate = convertDates(
    date,
    'jYYYY/jMM/jDD HH:mm',
    'YYYY/MM/DD HH:mm'
  );

  return {
    en: gregorianDate,
    fa: date,
  };
};

export const getConvTableData = (apiObj) => [
  {
    id: 'name',
    content: apiObj.user.name,
  },
  {
    id: 'firstOnline',
    content: getDateBasedOnLocale(apiObj.user.firstVisit),
  },
  {
    id: 'lastOnline',
    content: getDateBasedOnLocale(apiObj.user.lastVisit),
  },
  {
    id: 'conversations',
    content: apiObj.chatsCount,
  },
  {
    id: 'email',
    content: apiObj.user.email,
  },
  {
    id: 'city',
    content: apiObj.user.location.city,
  },
];

export const getConvsArchiveData = (apiObj) => ({
  totalConvsCount: apiObj.totalConversationCount,
  convsList: apiObj.conversations.map((item) => ({
    id: item.id,
    value: getConvTableData(item),
  })),
});

const getMessageType = (type) => {
  switch (type) {
    case 0:
      return 'image';
    case 1:
      return 'video';
    case 2:
      return 'audio';
    case 3:
      return 'compress';
    case 4:
      return 'file';
    default:
      return 'text';
  }
};

const bytesToSize = (bytes) => {
  let sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
};

const getOtherProps = (chat) => {
  const message = {};
  const chatType = chat.type;

  // special means anything other than text
  const isChatTypeSpecial = typeof chatType === 'number' && chatType !== 6;

  if (isChatTypeSpecial) {
    message.url = `${RAYCHAT_UPLOAD_FILE_URL}/${chat.message}`;
    if (chat.meta.size) {
      message.size = bytesToSize(chat.meta.size);
    }
    if (chat.meta.accessibleSize) {
      message.accessibleSize = chat.meta.accessibleSize;
    }

    // file messages
    if ([4, 3].includes(chatType)) {
      message.name = chat.meta.name;
    }
  }

  return message;
};

const getConvertedChatsThread = (chats) =>
  chats.map((chat) => ({
    id: chat.id,
    time: getDateBasedOnLocale(toEnDigit(chat.persianDate)),
    content: chat.message,
    isAgent: chat.isAgent,
    avatar: chat.user.avatar,
    name: chat.user.name,
    replyId: chat.replyId,
    edited: chat.edited,
    type: getMessageType(chat.type),
    ...getOtherProps(chat),
  }));

const getConvertedChats = (apiObj) => {
  return Object.entries(apiObj.chats).map(([date, chats]) => ({
    name: apiObj.user.name,
    page: getDecodedURL(apiObj.user.lastVisitedPage),
    convDate: date,
    chatsThread: getConvertedChatsThread(chats.chats),
  }));
};

export const getUserConvsArchiveDataFromApi = (apiObj) => ({
  userId: apiObj.user.id,
  avatar: apiObj.user.avatar,
  name: apiObj.user.name,
  isUserOnline: !!apiObj.user.online,
  phone: apiObj.user.phone,
  email: apiObj.user.email,
  about: apiObj.user.about || '',
  lastVisitedPage: getDecodedURL(apiObj.user.lastVisitedPage),
  ip: apiObj.user.location.ip,
  browser: apiObj.user.browser.name,
  browserVersion: apiObj.user.browser.ver,
  os: apiObj.user.browser.os,
  isUserOnMobile: apiObj.user.browser.mobile,
  referrer: apiObj.user.referrer,
  conversations: getConvertedChats(apiObj),
});

const getDateSearchType = (filterType) => {
  const SEARCH_TYPE_PER_FILTERTYPE = {
    updatedAtDate: 'updatedAt',
    emailAndUpdatedAtDate: 'updatedAt',
  };

  // by default searchType value is createdAt
  const searchType = SEARCH_TYPE_PER_FILTERTYPE[filterType] || 'createdAt';

  return searchType;
};

const getFilterTypeForApi = (type) => {
  const FILTER_TYPES_FOR_API = {
    email: 'email',
    openConversations: 'openConversations',
    createdAtDate: 'date',
    updatedAtDate: 'date',
    emailAndCreatedAtDate: 'dateAndEmail',
    emailAndUpdatedAtDate: 'dateAndEmail',
    username: 'name',
    userCreationDate: 'userDate',
    dateAndText: 'dateAndText',
  };

  return FILTER_TYPES_FOR_API[type];
};

const getDateRangeObject = (data) => {
  // empty object if filter type does not need date range
  if (['email', 'openConversations'].includes(data.filterType)) return {};

  return {
    from: getProperDate(data.startDate),
    to: getProperDate(data.endDate),
  };
};

const getFiltersApiObject = (data) => {
  const { filterType } = data;
  const dateSearchType = getDateSearchType(data.filterType);
  const type = getFilterTypeForApi(filterType);
  const dateRangeObject = getDateRangeObject(data);

  if (filterType === 'email') {
    return {
      type,
      email: data.email,
    };
  }

  if (filterType === 'openConversations') {
    return {
      type,
      agentsId: [data.operator],
    };
  }

  if (['emailAndCreatedAtDate', 'emailAndUpdatedAtDate'].includes(filterType)) {
    return {
      type,
      dateSearchType,
      email: data.email,
      ...dateRangeObject,
    };
  }

  if (filterType === 'username') {
    return {
      type,
      name: data.name,
    };
  }

  if (filterType === 'userCreationDate') {
    return {
      type,
      dateSearchType,
      ...dateRangeObject,
    };
  }

  if (filterType === 'dateAndText') {
    return {
      type,
      text: data.text,
      ...dateRangeObject,
    };
  }

  return {
    type,
    dateSearchType,
    ...dateRangeObject,
  };
};

// returns payload for users convs excel export api
export const getUsersConvsExcelExportPayload = (data) =>
  getApiReadyFilterOptions(data);

export const getApiReadyFilterOptions = (data) => ({
  channelId: data.channelId,
  skip: data.currentPage,
  limit: data.pageSize,
  filter: getFiltersApiObject(data),
});

export const getApiPayloadForExcelExport = (data) =>
  getApiReadyFilterOptions(data);
